import React, {StrictMode, FC, useEffect} from 'react'
import {createRoot} from 'react-dom/client'
import {I18nextProvider, useTranslation} from 'react-i18next'
import {BrowserRouter, matchPath, useLocation} from 'react-router-dom'

import {CssBaseline, useTheme} from '@mui/material'
import GlobalStyles from '@mui/material/GlobalStyles'
import {StyledEngineProvider} from '@mui/material/styles'
import {LocalizationProvider} from '@mui/x-date-pickers'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import {EnhancedConditionalThemeProvider} from '@progynyglobal/pg-ui-library'
import * as Sentry from '@sentry/react'

import {HelpButton} from '@progyny/components/Buttons/HelpButton'
import DynamicThemeProvider from '@progyny/components/DynamicThemeProvider'
import Modal from '@progyny/components/Modal'
import {Navigation} from '@progyny/components/Navigation'
import {Container as PortalContainer} from '@progyny/components/Portal'
import SessionExpiredModal from '@progyny/components/SessionExpiredModal'
import GraphQLWrapper from '@progyny/hocs/GraphQLWrapper'
import {useProductLine} from '@progyny/hooks/useProductLine'
import {UserProvider} from '@progyny/providers/UserProvider'
import Routes, {useRoutes} from '@progyny/routes'
import {useLanguageStore} from '@progyny/store/languageStore'
import {SnackbarProviderWrapper} from '@progyny/utils/SnackbarProvider'
import {
  REACT_APP_SENTRY_DSN,
  isDevelopment,
  isProduction,
  isStaging,
} from '@progyny/utils/env'

import i18n from '@root/i18n'

import {
  initialNavigationState,
  useNavigationStore,
} from './store/navigationStore'
import {globalCss} from './utils/globalStyles'

const container = document.getElementById('root')
const root = createRoot(container!)

type Props = {
  children: React.ReactNode
}
const WithStrictMode = ({children}: Props) => {
  if (isDevelopment) {
    return <StrictMode>{children}</StrictMode>
  } else return <>{children}</>
}

if ((isStaging || isProduction) && REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: REACT_APP_SENTRY_DSN,
    environment: isStaging ? 'staging' : 'production',
  })
}

// Component to sync navigation store with the current route's withNavigation config
const NavigationInitializer: FC = () => {
  const {routes} = useRoutes()
  const location = useLocation()
  const mergeNav = useNavigationStore((state) => state.mergeNav)
  const replaceNav = useNavigationStore((state) => state.replaceNav)

  useEffect(() => {
    const currentRoute = Object.values(routes).find(
      (route) =>
        matchPath(location.pathname, {path: route.path, exact: false}) !== null
    )
    if (currentRoute?.withNavigation) {
      mergeNav(currentRoute.withNavigation)
    } else {
      replaceNav(initialNavigationState)
    }
  }, [location.pathname, routes, mergeNav, replaceNav])

  return null
}

// Component to initialize language store from i18n
const LanguageStoreInitializer: FC = () => {
  const {i18n} = useTranslation()
  const {initializeLanguage} = useLanguageStore()

  // Initialize language store when i18n language changes
  useEffect(() => {
    const currentLanguage =
      localStorage.getItem('ui_language') || i18n.language || 'en'
    initializeLanguage(currentLanguage)
  }, [i18n.language, initializeLanguage])

  return null
}

// Component that wraps children with theme provider, using productLine from me query
// This component needs to be inside DynamicThemeProvider to access the theme
const AppWithTheme: FC<{children: React.ReactNode}> = ({children}) => {
  const {productLine} = useProductLine()
  const theme = useTheme()

  return (
    <EnhancedConditionalThemeProvider
      key={productLine} // Force re-render when productLine changes
      productLine={productLine}
      enableProductLineContext={true}
      theme={theme}
    >
      {children}
    </EnhancedConditionalThemeProvider>
  )
}

const AppWithProductLine: FC = () => {
  return (
    <DynamicThemeProvider>
      <CssBaseline />
      <GlobalStyles styles={globalCss} />
      <SnackbarProviderWrapper>
        <BrowserRouter>
          <GraphQLWrapper>
            <I18nextProvider i18n={i18n}>
              <LanguageStoreInitializer />
              <UserProvider>
                <AppWithTheme>
                  <NavigationInitializer />
                  <Navigation />
                  <Routes />
                  <Modal />
                  <SessionExpiredModal />
                  <HelpButton />
                </AppWithTheme>
              </UserProvider>
            </I18nextProvider>
          </GraphQLWrapper>
        </BrowserRouter>
      </SnackbarProviderWrapper>
      <PortalContainer />
    </DynamicThemeProvider>
  )
}

root.render(
  <WithStrictMode>
    <StyledEngineProvider injectFirst>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en-gb'>
        <AppWithProductLine />
      </LocalizationProvider>
    </StyledEngineProvider>
  </WithStrictMode>
)
